import { z } from "zod";

const dateTimeSchema = z.string().datetime({ offset: true });
const dateSchema = z.string().date();
const identifierSchema = z.string().trim().min(1).max(256);
function jsonArray<Schema extends z.ZodType>(item: Schema) {
  // Go 空切片序列化为 JSON null；只把 null 收成 []，不把缺失金额收成 0。
  return z.union([z.array(item), z.null()]).transform((value) => value ?? []);
}
const decimalSchema = z.string().regex(/^-?\d+(?:\.\d+)?$/u);
const integerStringSchema = z.string().regex(/^-?\d+$/u);
const countSchema = z.number().int().nonnegative();
const currencySchema = z.string().regex(/^[A-Z]{3}$/u);
const overviewCoverageStateSchema = z.enum(["complete", "partial", "empty"]);
const metricCoverageStateSchema = z.enum([
  "complete",
  "partial",
  "missing",
  "not_applicable",
]);

const queryScopeWireSchema = z
  .object({
    anchor_date: z.union([
      dateSchema,
      z.string().regex(/^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/u),
    ]),
    comparison_end_exclusive: dateTimeSchema,
    comparison_start: dateTimeSchema,
    display_currency: currencySchema,
    range_kind: z.enum(["today", "7d", "30d", "custom"]),
    reporting_timezone: z.string().trim().min(1).max(80),
    window_end_exclusive: dateTimeSchema,
    window_start: dateTimeSchema,
  })
  .strict();

const metricCoverageWireSchema = z
  .object({
    covered: countSchema,
    expected: countSchema,
    key: identifierSchema,
    reason_codes: jsonArray(identifierSchema),
    state: metricCoverageStateSchema,
  })
  .strict()
  .refine((coverage) => coverage.covered <= coverage.expected, {
    message: "指标覆盖数量不能超过预期数量",
  });

const coverageWireSchema = z
  .object({
    metrics: jsonArray(metricCoverageWireSchema),
    reason_codes: jsonArray(identifierSchema),
    sources: z
      .object({
        covered: countSchema,
        expected: countSchema,
        missing: countSchema,
      })
      .strict()
      .refine(
        (sources) =>
          sources.covered <= sources.expected &&
          sources.missing === sources.expected - sources.covered,
        { message: "来源覆盖数量不一致" },
      ),
    state: overviewCoverageStateSchema,
  })
  .strict();

const headlineWireSchema = z
  .object({
    state: overviewCoverageStateSchema,
    summary: z.string().trim().min(1).max(600),
    title: z.string().trim().min(1).max(240),
  })
  .strict();

const kpiWireSchema = z
  .object({
    calculation_revision: identifierSchema,
    comparison_delta_bps: integerStringSchema.nullable(),
    comparison_value: integerStringSchema.nullable(),
    coverage_state: metricCoverageStateSchema,
    currency: currencySchema.nullable(),
    key: identifierSchema,
    label: z.string().trim().min(1).max(80),
    missing_reason_code: z.string().trim().max(160),
    unit: z.enum(["minor", "count", "basis_points"]),
    value: integerStringSchema.nullable(),
  })
  .strict();

const rankingItemWireSchema = z
  .object({
    currency: currencySchema,
    is_other_group: z.boolean(),
    label: z.string().trim().min(1).max(160),
    market: z.string().trim().max(80),
    provider: z.string().trim().max(80),
    resource_id: identifierSchema,
    share_bps: integerStringSchema.nullable(),
    mock_metrics: z.object({ profit_minor: integerStringSchema, growth_bps: integerStringSchema, ad_efficiency_bps: integerStringSchema }).strict().optional(),
    source_count: countSchema,
    value: integerStringSchema,
  })
  .strict();

const attentionItemWireSchema = z
  .object({
    id: identifierSchema,
    kind: z.enum(["product_finding", "low_stock"]),
    product_id: z.string().trim().max(256),
    provider: z.string().trim().max(80),
    resource_id: z.string().trim().max(256),
    severity: z.enum(["critical", "warning", "info"]),
    source_ref_id: z.string().trim().max(256),
    title: z.string().trim().min(1).max(320),
  })
  .strict();

const seriesWireSchema = z
  .object({
    currency: currencySchema,
    key: identifierSchema,
    points: z.array(
      z
        .object({
          currency: currencySchema,
          end_exclusive: dateTimeSchema,
          start: dateTimeSchema,
          value: integerStringSchema,
        })
        .strict(),
    ),
    unit: z.enum(["minor", "count", "basis_points"]),
  })
  .strict();

const compositionItemWireSchema = z
  .object({
    currency: currencySchema,
    key: identifierSchema,
    label: z.string().trim().min(1).max(160),
    share_bps: integerStringSchema,
    value: integerStringSchema,
  })
  .strict();

const sourceRefWireSchema = z
  .object({
    family: identifierSchema,
    id: identifierSchema,
    provider: identifierSchema,
    resource_id: identifierSchema,
    resource_kind: identifierSchema,
    revision: identifierSchema,
    source_as_of: dateTimeSchema,
    source_revision: identifierSchema,
  })
  .strict();

const fxRefWireSchema = z
  .object({
    base_currency: currencySchema,
    id: identifierSchema,
    inverted: z.boolean(),
    observed_at: dateTimeSchema,
    quote_currency: currencySchema,
    rate: decimalSchema,
    source: identifierSchema,
    source_revision: identifierSchema,
  })
  .strict();

const storeOverviewWireSchema = z
  .object({
    actions: z
      .object({
        can_create_temporary_conversation: z.boolean(),
        can_export_report: z.boolean(),
        report_requires_snapshot: z.boolean(),
        temporary_conversation_scope: z.literal(
          "organization_store_overview/read_only",
        ),
      })
      .strict(),
    alerts: jsonArray(attentionItemWireSchema),
    as_of: dateTimeSchema.nullable(),
    attention: z
      .union([z.array(attentionItemWireSchema).max(3), z.null()])
      .transform((value) => value ?? []),
    composition: z
      .object({
        market: z.array(compositionItemWireSchema),
        platform: z.array(compositionItemWireSchema),
      })
      .strict(),
    coverage: coverageWireSchema,
    fx_refs: jsonArray(fxRefWireSchema),
    headline: headlineWireSchema,
    kpis: z.array(kpiWireSchema).length(5),
    query_scope: queryScopeWireSchema,
    rankings: z.object({ stores: z.array(rankingItemWireSchema).max(9) }).strict(),
    schema_version: z.literal(1),
    series: jsonArray(seriesWireSchema),
    snapshot_id: identifierSchema,
    source_refs: jsonArray(sourceRefWireSchema),
  })
  .strict()
  .superRefine((overview, context) => {
    const keys = overview.kpis.map((kpi) => kpi.key);
    if (new Set(keys).size !== keys.length) {
      context.addIssue({
        code: "custom",
        message: "KPI key 不能重复",
        path: ["kpis"],
      });
    }
  });

export const temporaryStoreOverviewConversationResponseSchema = z
  .object({
    context_scope: z.literal("organization_store_overview/read_only"),
    conversation_id: identifierSchema,
    lifecycle_state: z.literal("active"),
    schema_version: z.literal(1),
    snapshot_id: identifierSchema,
  })
  .strict()
  .transform((response) => ({
    contextScope: response.context_scope,
    conversationId: response.conversation_id,
    lifecycleState: response.lifecycle_state,
    schemaVersion: response.schema_version,
    snapshotId: response.snapshot_id,
  }));

export const storeOverviewReportResponseSchema = z
  .object({
    failure_code: z.string().trim().max(160).nullable(),
    job_id: identifierSchema,
    resource_id: identifierSchema.nullable(),
    schema_version: z.literal(1),
    snapshot_id: identifierSchema,
    status: z.enum(["queued", "processing", "available", "failed"]),
  })
  .strict()
  .superRefine((response, context) => {
    if (response.status === "available" && !response.resource_id) {
      context.addIssue({
        code: "custom",
        message: "报告可用时必须包含云盘资源",
        path: ["resource_id"],
      });
    }
    if (response.status === "failed" && !response.failure_code) {
      context.addIssue({
        code: "custom",
        message: "报告失败时必须包含失败代码",
        path: ["failure_code"],
      });
    }
  })
  .transform((response) => ({
    failureCode: response.failure_code,
    jobId: response.job_id,
    resourceId: response.resource_id,
    schemaVersion: response.schema_version,
    snapshotId: response.snapshot_id,
    status: response.status,
  }));

function attentionItemFromWire(
  item: z.infer<typeof attentionItemWireSchema>,
) {
  return {
    id: item.id,
    kind: item.kind,
    productId: item.product_id,
    provider: item.provider,
    resourceId: item.resource_id,
    severity: item.severity,
    sourceRefId: item.source_ref_id,
    title: item.title,
  };
}

function compositionItemFromWire(
  item: z.infer<typeof compositionItemWireSchema>,
) {
  return {
    currency: item.currency,
    key: item.key,
    label: item.label,
    shareBps: item.share_bps,
    value: item.value,
  };
}

export const storeOverviewResponseSchema = storeOverviewWireSchema.transform(
  (response) => ({
    actions: {
      canCreateTemporaryConversation:
        response.actions.can_create_temporary_conversation,
      canExportReport: response.actions.can_export_report,
      reportRequiresSnapshot: response.actions.report_requires_snapshot,
      temporaryConversationScope:
        response.actions.temporary_conversation_scope,
    },
    alerts: response.alerts.map(attentionItemFromWire),
    asOf: response.as_of,
    attention: response.attention.map(attentionItemFromWire),
    composition: {
      market: response.composition.market.map(compositionItemFromWire),
      platform: response.composition.platform.map(compositionItemFromWire),
    },
    coverage: {
      metrics: response.coverage.metrics.map((metric) => ({
        covered: metric.covered,
        expected: metric.expected,
        key: metric.key,
        reasonCodes: metric.reason_codes,
        state: metric.state,
      })),
      reasonCodes: response.coverage.reason_codes,
      sources: response.coverage.sources,
      state: response.coverage.state,
    },
    fxRefs: response.fx_refs.map((ref) => ({
      baseCurrency: ref.base_currency,
      id: ref.id,
      inverted: ref.inverted,
      observedAt: ref.observed_at,
      quoteCurrency: ref.quote_currency,
      rate: ref.rate,
      source: ref.source,
      sourceRevision: ref.source_revision,
    })),
    headline: response.headline,
    kpis: response.kpis.map((kpi) => ({
      calculationRevision: kpi.calculation_revision,
      comparisonDeltaBps: kpi.comparison_delta_bps,
      comparisonValue: kpi.comparison_value,
      coverageState: kpi.coverage_state,
      currency: kpi.currency,
      key: kpi.key,
      label: kpi.label,
      missingReasonCode: kpi.missing_reason_code,
      unit: kpi.unit,
      value: kpi.value,
    })),
    queryScope: {
      anchorDate: response.query_scope.anchor_date,
      comparisonEndExclusive: response.query_scope.comparison_end_exclusive,
      comparisonStart: response.query_scope.comparison_start,
      displayCurrency: response.query_scope.display_currency,
      rangeKind: response.query_scope.range_kind,
      reportingTimezone: response.query_scope.reporting_timezone,
      windowEndExclusive: response.query_scope.window_end_exclusive,
      windowStart: response.query_scope.window_start,
    },
    rankings: {
      stores: response.rankings.stores.map((item) => ({
        currency: item.currency,
        isOtherGroup: item.is_other_group,
        label: item.label,
        market: item.market,
        provider: item.provider,
        resourceId: item.resource_id,
        shareBps: item.share_bps,
        mockMetrics: item.mock_metrics,
        sourceCount: item.source_count,
        value: item.value,
      })),
    },
    schemaVersion: response.schema_version,
    series: response.series.map((series) => ({
      currency: series.currency,
      key: series.key,
      points: series.points.map((point) => ({
        currency: point.currency,
        endExclusive: point.end_exclusive,
        start: point.start,
        value: point.value,
      })),
      unit: series.unit,
    })),
    snapshotId: response.snapshot_id,
    sourceRefs: response.source_refs.map((ref) => ({
      family: ref.family,
      id: ref.id,
      provider: ref.provider,
      resourceId: ref.resource_id,
      resourceKind: ref.resource_kind,
      revision: ref.revision,
      sourceAsOf: ref.source_as_of,
      sourceRevision: ref.source_revision,
    })),
  }),
);

export type StoreOverview = z.infer<typeof storeOverviewResponseSchema>;
export type StoreOverviewKpi = StoreOverview["kpis"][number];

export function parseStoreOverviewResponse(input: unknown) {
  return storeOverviewResponseSchema.parse(input);
}
